import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

interface Invoice {
  id: number | string;
  numero: string;
  client: {
    raison_sociale: string;
    email: string;
  };
  total_ttc: number;
  montant_paye: number;
  date_echeance: string;
  bon_livraison?: {
    numero: string;
  };
}

interface PaymentFormProps {
  invoice: Invoice | null;
  onClose: () => void;
  onSave: (paymentData: any) => void;
}

const PAYMENT_METHODS = [
  { value: 'VIREMENT', label: 'Virement bancaire' },
  { value: 'CHEQUE', label: 'Chèque' },
  { value: 'ESPECES', label: 'Espèces' },
  { value: 'MOBILE_MONEY', label: 'Mobile Money' },
  { value: 'CARTE', label: 'Carte bancaire' },
];

export default function PaymentForm({ invoice, onClose, onSave }: PaymentFormProps) {
  const remainingAmount = useMemo(() => {
    if (!invoice) return 0;
    return Number(invoice.total_ttc) - Number(invoice.montant_paye || 0);
  }, [invoice]);

  const [formData, setFormData] = useState({
    montant: remainingAmount,
    mode: 'VIREMENT' as string,
    reference_externe: '',
    date_paiement: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (invoice) {
      setFormData(prev => ({
        ...prev,
        montant: remainingAmount,
      }));
      setErrors({});
    }
  }, [invoice, remainingAmount]);

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' GNF';
  };

  const generateReference = () => {
    const prefixes: Record<string, string> = {
      VIREMENT: 'VIR',
      CHEQUE: 'CHQ',
      ESPECES: 'ESP',
      MOBILE_MONEY: 'MM',
      CARTE: 'CB',
    };

    const prefix = prefixes[formData.mode] || 'REG';
    const date = new Date().toISOString().slice(2, 10).replace(/-/g, '');
    const random = Math.floor(Math.random() * 999).toString().padStart(3, '0');

    setFormData(prev => ({
      ...prev,
      reference_externe: `${prefix}-${date}-${random}`,
    }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.montant || formData.montant <= 0) {
      newErrors.montant = 'Montant requis et supérieur à 0';
    }
    if (formData.montant > remainingAmount) {
      newErrors.montant = `Ne peut dépasser ${formatPrice(remainingAmount)}`;
    }
    if (!formData.date_paiement) {
      newErrors.date_paiement = 'Date requise';
    }
    if (formData.mode !== 'ESPECES' && !formData.reference_externe.trim()) {
      newErrors.reference_externe = 'Référence requise pour ce mode';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice || !validate()) return;

    setLoading(true);
    try {
      onSave({
        facture_client_id: invoice.id,
        montant: formData.montant,
        mode: formData.mode,
        date_paiement: formData.date_paiement,
        reference_externe: formData.reference_externe || null,
        notes: formData.notes || null,
      });
    } catch (err) {
      toast.error('Erreur lors de l’enregistrement');
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 max-w-2xl w-full max-h-[95vh] overflow-y-auto">

        {/* ================= HEADER ================= */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <i className="ri-money-euro-circle-line text-4xl text-green-600"></i>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Enregistrer un règlement</h2>
                <p className="text-lg text-gray-700 mt-1">Facture N° {invoice.numero}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-3 hover:bg-gray-100 rounded-lg transition"
            >
              <i className="ri-close-line text-2xl text-gray-600"></i>
            </button>
          </div>
        </div>

        {/* ================= RÉCAP FACTURE ================= */}
        <div className="p-6 bg-green-50 border-b border-green-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-lg">
            <div>
              <p className="text-gray-600">Client</p>
              <p className="font-bold text-gray-900">
                {invoice.client?.raison_sociale ?? 'Client non renseigné'}
              </p>

              <p className="text-sm text-gray-600">
                {invoice.client?.email ?? '—'}
              </p>

            </div>
            <div>
              <p className="text-gray-600">Bon de livraison</p>
              <p className="font-bold text-blue-700">
                {invoice.bon_livraison?.numero || '—'}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Total facture</p>
              <p className="text-xl font-bold text-blue-600">{formatPrice(invoice.total_ttc)}</p>
            </div>
            <div>
              <p className="text-gray-600">Reste à payer</p>
              <p className={`text-2xl font-bold ${remainingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatPrice(remainingAmount)}
              </p>
            </div>
          </div>
        </div>

        {/* ================= FORMULAIRE ================= */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Montant + Mode */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Montant du règlement *
              </label>
              <input
                type="number"
                value={formData.montant}
                onChange={(e) => setFormData(prev => ({ ...prev, montant: Number(e.target.value) || 0 }))}
                min="0.01"
                max={remainingAmount}
                step="0.01"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                required
              />
              {errors.montant && <p className="text-sm text-red-600 mt-1">{errors.montant}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mode de paiement *
              </label>
              <select
                value={formData.mode}
                onChange={(e) => setFormData(prev => ({ ...prev, mode: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                {PAYMENT_METHODS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date du règlement *
            </label>
            <input
              type="date"
              value={formData.date_paiement}
              onChange={(e) => setFormData(prev => ({ ...prev, date_paiement: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              required
            />
          </div>

          {/* Référence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Référence {formData.mode !== 'ESPECES' ? '*' : '(facultatif)'}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.reference_externe}
                onChange={(e) => setFormData(prev => ({ ...prev, reference_externe: e.target.value }))}
                placeholder="ex: VIR-250101-123"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
              <button
                type="button"
                onClick={generateReference}
                className="px-4 py-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition flex items-center gap-2"
              >
                <i className="ri-refresh-line"></i>
                Générer
              </button>
            </div>
            {errors.reference_externe && <p className="text-sm text-red-600 mt-1">{errors.reference_externe}</p>}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (facultatif)
            </label>
            <textarea
              rows={4}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Commentaire interne, référence banque, etc."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>

          {/* ================= ACTIONS ================= */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition flex items-center gap-2 disabled:opacity-50"
            >
              {loading && <i className="ri-loader-4-line animate-spin"></i>}
              Enregistrer le règlement
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}